import { useState } from 'react';
import { useStore } from '../store/useStore';
import {
  appleSignInOffered,
  sendEmailLink,
  signInApple,
  signInGithub,
  signInGoogle,
} from '../lib/auth';
import { useT } from '../i18n/useT';
import { Modal } from './Modal';

/**
 * Sign-in dialog (phase B). Accounts are optional — this only appears when the
 * user chooses to sign in, to enable cross-device sync. Google / GitHub via
 * popup, or a passwordless email magic-link.
 */
export function AuthDialog() {
  const t = useT();
  const open = useStore((s) => s.authOpen);
  const setOpen = useStore((s) => s.setAuthOpen);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const runProvider = (fn: () => Promise<unknown>) => async () => {
    setError(null);
    setBusy(true);
    try {
      await fn();
      setOpen(false);
    } catch (e) {
      // A provider that exists in the app but not yet in the Firebase project
      // fails with a code, not with anything a person would want to read.
      const code = (e as { code?: string } | null)?.code;
      setError(
        code === 'auth/operation-not-allowed'
          ? t('auth.appleUnavailable')
          : e instanceof Error
            ? e.message
            : t('auth.signinFail'),
      );
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError(null);
    setBusy(true);
    try {
      await sendEmailLink(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.linkFail'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={() => setOpen(false)} title={t('auth.dialogTitle')} label={t('auth.title')}>
      <p className="mb-modal-note">{t('auth.optional')}</p>

      {sent ? (
        <p className="mb-modal-note">{t('auth.checkInbox', { email })}</p>
      ) : (
        <>
          <div className="mb-auth-providers">
            <button className="mb-btn" disabled={busy} onClick={runProvider(signInGoogle)}>
              {t('auth.google')}
            </button>
            <button className="mb-btn" disabled={busy} onClick={runProvider(signInGithub)}>
              {t('auth.github')}
            </button>
            {appleSignInOffered() && (
              <button className="mb-btn" disabled={busy} onClick={runProvider(signInApple)}>
                {t('auth.apple')}
              </button>
            )}
          </div>
          <div className="mb-auth-divider">{t('auth.or')}</div>
          <form className="mb-auth-email" onSubmit={submitEmail}>
            <input
              type="email"
              required
              placeholder={t('auth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label={t('auth.emailLabel')}
            />
            <button className="mb-btn mb-btn-primary" type="submit" disabled={busy}>
              {t('auth.sendLink')}
            </button>
          </form>
        </>
      )}

      {error && (
        <p className="mb-modal-note" style={{ color: 'var(--mb-danger)' }}>
          {error}
        </p>
      )}
    </Modal>
  );
}
